import { isSuccess } from '@/context/services/type';
import {
  ServiceError,
  normalizeServiceError,
} from '@/context/services/utils/extractServiceError';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  GetAdminInvoiceReportSummaryResponse,
  useGetAdminInvoiceReportSummary,
} from 'ap-openapi';
import { useMemo } from 'react';

export type { GetAdminInvoiceReportSummaryResponse };

export type AdminInvoiceReportSummaryError = ServiceError;

export type UseAdminInvoiceReportSummaryResult = {
  data?: GetAdminInvoiceReportSummaryResponse;
  error?: AdminInvoiceReportSummaryError;
  isLoading: boolean;
};

export const useAdminInvoiceReportSummary = (
  reportId: string
): UseAdminInvoiceReportSummaryResult => {
  const { data, error, isLoading } = useGetAdminInvoiceReportSummary(reportId);
  const body = data?.data;
  const status = data?.status;
  const isSuccessStatus = isSuccess(status);

  return useMemo(() => {
    if (isLoading) {
      return { isLoading: true };
    }

    if (error) {
      return {
        error: normalizeServiceError(error),
        isLoading: false,
      };
    }

    if (!isSuccessStatus) {
      return {
        error: {
          messages:
            typeof body === 'object' && isCommonError(body)
              ? body.messages
              : undefined,
          code: status?.toString(),
        },
        isLoading: false,
      };
    }

    if (body === undefined) {
      return {
        error: {
          messages: undefined,
          code: status?.toString(),
        },
        isLoading: false,
      };
    }

    return { data: body, isLoading: false };
  }, [body, status, isSuccessStatus, isLoading, error]);
};
